import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';

/**
 * SiteFooter — slim landing-page footer.
 *
 * Pre-redesign this had 16 links spread across 4 columns, but most just
 * redirected unauthenticated visitors to /login (Browse Library, Search,
 * Leaderboard, Community, My Library, Settings, etc.) which felt like
 * dead-ends for first-time visitors. Slimmed down to the 6 links that
 * are actually useful BEFORE sign-in: FAQ, Sign In, Contact Us, For Authors,
 * Privacy, Terms.
 */

const LINKS = [
  { label: 'FAQ', href: '#landing-faq-section', kind: 'anchor' },
  { label: 'About Us', to: '/about' },
  { label: 'Sign Up', to: '/signup' },
  { label: 'Sign In', to: '/login' },
  { label: 'For Authors', mailto: 'info@sevenchapterbooks.com?subject=Author%20Submission' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms of Use', to: '/terms' },
];

const FooterLink = ({ link }) => {
  const navigate = useNavigate();
  const cls = 'text-gray-400 hover:text-white text-sm underline-offset-2 hover:underline transition-colors';
  const tid = `footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`;
  if (link.mailto) {
    return <a href={`mailto:${link.mailto}`} className={cls} data-testid={tid}>{link.label}</a>;
  }
  if (link.kind === 'anchor') {
    return <a href={link.href} className={cls} data-testid={tid}>{link.label}</a>;
  }
  return (
    <button type="button" onClick={() => navigate(link.to)} className={`${cls} text-left`} data-testid={tid}>
      {link.label}
    </button>
  );
};

const SiteFooter = () => {
  const navigate = useNavigate();
  return (
    <footer
      className="py-10 px-4 sm:px-6 lg:px-8 bg-black border-t border-white/10"
      data-testid="landing-site-footer"
    >
      <div className="max-w-screen-md mx-auto text-gray-400">
        <button
          type="button"
          onClick={() => navigate('/contact')}
          className="text-sm hover:text-white underline underline-offset-4 inline-flex items-center gap-1.5 mb-6"
          data-testid="footer-questions-contact"
        >
          <Mail className="h-3.5 w-3.5" />
          Questions? Contact us.
        </button>

        <nav className="flex flex-wrap gap-x-6 gap-y-3 mb-8" aria-label="Site footer">
          {LINKS.map((link, i) => (
            <FooterLink key={i} link={link} />
          ))}
        </nav>

        <p className="text-xs text-gray-500">
          © 2024–2026 Seven Chapter Books. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default SiteFooter;
